import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';
import { selectActiveOrganizationId } from '@/store/selectors/authorizationSelectors';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { TemplateAudience, TemplateScope, TemplateStatus } from '@/domain/entities/preperty-template';
import { formatUnixDateTimeCompact } from '@/lib';
import { makeTemplateListScopeKey, type TemplateOrgScope } from '@/features/property-templates/propertyTemplatesTypes';

import {
  fetchPropertyTemplates,
  deleteTemplate,
  publishTemplate,
  deprecateTemplate,
  cloneTemplate,
} from '@/features/property-templates/propertyTemplatesThunks';
import {
  selectActiveAudience,
  selectActiveScope,
  selectActiveOrgScope,
  selectSortedFilteredPropertyTemplates,
  setPropertyTemplatesActiveAudience,
  setPropertyTemplatesActiveScope,
  setPropertyTemplatesOrgScope,
  setPropertyTemplatesSort,
  setTemplatesActiveStatus,
} from '@/features/property-templates/propertyTemplatesSlice';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUSES: (TemplateStatus | 'all')[] = ['all', 'draft', 'published', 'deprecated'];

const TemplatesPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();
  const { hasPermission, isSuperAdmin, isSystemUser } = usePermissions();

  const canCreateTemplate = hasPermission(Permissions.TemplatesCreate);
  const canUpdateTemplate = hasPermission(Permissions.TemplatesUpdate);
  const canDeleteTemplate = hasPermission(Permissions.TemplatesDelete);

  const activeStatus = useAppSelector((s) => s.propertyTemplates.activeStatus);
  const activeAudience = useAppSelector(selectActiveAudience);
  const activeScope = useAppSelector(selectActiveScope);
  const templateOrgScope = useAppSelector(selectActiveOrgScope);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  // Super admins load their authorized all-org view once, then apply the active-org filter locally.
  // This keeps system templates whose required orgID matches the active organization in draft/deprecated tabs.
  const templateListOrgID = isSuperAdmin ? undefined : (activeOrganizationId ?? undefined);
  const templateListScopeKey = useMemo(() => makeTemplateListScopeKey(templateListOrgID), [templateListOrgID]);

  const templatesForAudience = useAppSelector(selectSortedFilteredPropertyTemplates);
  const templates = useMemo(() => {
    if (!isSuperAdmin || templateOrgScope === 'all-orgs') return templatesForAudience;
    if (!activeOrganizationId) return [];
    return templatesForAudience.filter((template) => template.orgID === activeOrganizationId);
  }, [activeOrganizationId, isSuperAdmin, templateOrgScope, templatesForAudience]);

  const hasFetched = useAppSelector((s) => s.propertyTemplates.hasFetchedByStatus[activeStatus]);
  const isListing = useAppSelector((s) => s.propertyTemplates.isListingByStatus[activeStatus]);
  const error = useAppSelector((s) => s.propertyTemplates.errorByStatus[activeStatus]);
  const fetchedScopeKey = useAppSelector((s) => s.propertyTemplates.listScopeByStatus[activeStatus]);

  const isDeleting = useAppSelector((s) => s.propertyTemplates.isDeleting);
  const isPublishing = useAppSelector((s) => s.propertyTemplates.isPublishing);
  const isDeprecating = useAppSelector((s) => s.propertyTemplates.isDeprecating);
  const isCloning = useAppSelector((s) => s.propertyTemplates.isCloning);

  const sortKey = useAppSelector((s) => s.propertyTemplates.sortKey);
  const sortDir = useAppSelector((s) => s.propertyTemplates.sortDir);
  const isWaitingForOrganizationContext = (!isSystemUser || templateOrgScope === 'active-org') && !activeOrganizationId;
  const showOrganizationColumn = isSuperAdmin && templateOrgScope === 'all-orgs';
  const showScopeColumn = activeScope === 'all';
  const tableColumnCount = 5 + Number(showOrganizationColumn) + Number(showScopeColumn);

  useEffect(() => {
    if (isSuperAdmin && !activeOrganizationId && templateOrgScope !== 'all-orgs') {
      dispatch(setPropertyTemplatesOrgScope('all-orgs'));
    }
  }, [activeOrganizationId, dispatch, isSuperAdmin, templateOrgScope]);

  // ✅ only fetch when this status bucket wasn't fetched yet (prevents tab-switch races)
  useEffect(() => {
    if (isWaitingForOrganizationContext) return;
    if ((!hasFetched || fetchedScopeKey !== templateListScopeKey) && !isListing) {
      dispatch(fetchPropertyTemplates({ status: activeStatus, orgID: templateListOrgID }));
    }
  }, [
    activeStatus,
    dispatch,
    fetchedScopeKey,
    hasFetched,
    isListing,
    isWaitingForOrganizationContext,
    templateListOrgID,
    templateListScopeKey,
  ]);

  const renderSortArrow = (key: 'title' | 'status' | 'updatedAt' | 'audience') => {
    if (sortKey !== key) return <span className="text-muted-foreground ml-1 text-xs">↕</span>;
    return <span className="ml-1 text-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const onSort = (key: 'title' | 'status' | 'updatedAt' | 'audience') => {
    dispatch(setPropertyTemplatesSort(key));
  };

  const formatAudienceLabel = (aud?: TemplateAudience) => {
    if (aud === 'business') return 'Business';
    if (aud === 'consumer') return 'Mobile';
    if (aud === 'both') return 'Business + Mobile';
    return '-';
  };

  const onChangeStatus = (status: TemplateStatus | 'all') => {
    dispatch(setTemplatesActiveStatus(status));
  };

  const onChangeActiveAudience = (audience: TemplateAudience | 'all') => {
    dispatch(setPropertyTemplatesActiveAudience(audience));
  };

  const onChangeActiveScope = (scope: TemplateScope | 'all') => {
    dispatch(setPropertyTemplatesActiveScope(scope));
  };

  const handleEdit = (templateId: string) => {
    navigate(`/templates/${encodeURIComponent(templateId)}/edit`);
  };

  const handleRefresh = async () => {
    if (isListing) return;
    if (isWaitingForOrganizationContext) {
      toast.error('Organization context is still loading.');
      return;
    }

    try {
      await dispatch(fetchPropertyTemplates({ status: activeStatus, orgID: templateListOrgID })).unwrap();
      toast.success('Templates refreshed');
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err ? String(err.message) : 'Failed to load templates';
      toast.error(message);
    }
  };

  const handleDelete = async (templateId: string, status: TemplateStatus) => {
    if (!canDeleteTemplate) return toast.error('You do not have permission to delete templates.');
    if (status !== 'draft') return toast.error('Only draft templates can be deleted.');
    if (isDeleting) return;

    const ok = await confirm({
      title: 'Delete this draft template?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;

    const promise = dispatch(deleteTemplate({ id: templateId })).unwrap();

    toast.promise(promise, {
      loading: 'Deleting template…',
      success: 'Template deleted',
      error: (err) => err?.message || 'Failed to delete template',
    });

    promise
      .then(async () => {
        // refresh current bucket (safe even if user switched tabs)
        await dispatch(fetchPropertyTemplates({ status: 'draft', orgID: templateListOrgID }));
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handlePublish = async (templateId: string, status: TemplateStatus) => {
    if (!canUpdateTemplate) return toast.error('You do not have permission to update templates.');
    if (status !== 'draft') return;
    if (isPublishing) return;

    const ok = await confirm({
      title: 'Publish this template?',
      confirmLabel: 'Publish',
    });
    if (!ok) return;

    const promise = dispatch(publishTemplate({ id: templateId })).unwrap();

    toast.promise(promise, {
      loading: 'Publishing template…',
      success: 'Template published',
      error: (err) => err?.message || 'Failed to publish template',
    });

    promise
      .then(async () => {
        await Promise.all([
          dispatch(fetchPropertyTemplates({ status: 'draft', orgID: templateListOrgID })),
          dispatch(fetchPropertyTemplates({ status: 'published', orgID: templateListOrgID })),
        ]);
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handleDeprecate = async (templateId: string, status: TemplateStatus) => {
    if (!canUpdateTemplate) return toast.error('You do not have permission to update templates.');
    if (status !== 'published') return;
    if (isDeprecating) return;

    const ok = await confirm({
      title: 'Deprecate this template?',
      description: 'This will prevent future use but keep it for historical records.',
      confirmLabel: 'Deprecate',
      variant: 'destructive',
    });
    if (!ok) return;

    const promise = dispatch(deprecateTemplate({ id: templateId })).unwrap();

    toast.promise(promise, {
      loading: 'Deprecating template…',
      success: 'Template deprecated',
      error: (err) => err?.message || 'Failed to deprecate template',
    });

    promise
      .then(async () => {
        await Promise.all([
          dispatch(fetchPropertyTemplates({ status: 'published', orgID: templateListOrgID })),
          dispatch(fetchPropertyTemplates({ status: 'deprecated', orgID: templateListOrgID })),
        ]);
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handleClone = async (templateId: string, status: TemplateStatus) => {
    if (!canCreateTemplate) return toast.error('You do not have permission to create templates.');
    if (status === 'deprecated') return toast.error('Deprecated templates cannot be cloned.');
    if (isCloning) return;

    const ok = await confirm({
      title: 'Clone this template?',
      description: 'This will create a new draft copy you can edit.',
      confirmLabel: 'Clone',
    });
    if (!ok) return;

    const promise = (async () => {
      const res = await dispatch(cloneTemplate({ id: templateId })).unwrap();
      const newId = res.data?.id;
      if (!newId) {
        throw new Error('Clone succeeded but the API did not return the new template id.');
      }
      return { id: newId };
    })();

    toast.promise(promise, {
      loading: 'Cloning template…',
      success: 'Template cloned',
      error: (err) => err?.message || 'Failed to clone template',
    });

    promise
      .then(({ id }) => {
        navigate(`/templates/${encodeURIComponent(id)}/edit`);
      })
      .catch(() => {
        // toast already handled
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <PageHeader title="My Templates" />
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleRefresh()}
          disabled={isListing || isWaitingForOrganizationContext}
        >
          <RefreshCw className={isListing ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
          {isListing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
      <div className="flex items-start justify-between gap-3">
        <ErrorNotice className="flex-1" message={error} title="Failed to Load Templates" />
        {error ? (
          <Button type="button" variant="outline" onClick={() => void handleRefresh()} disabled={isListing}>
            {isListing ? 'Retrying…' : 'Retry'}
          </Button>
        ) : null}
      </div>

      {/* ✅ Segmented status switch */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <Button
              key={s}
              type="button"
              variant={activeStatus === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChangeStatus(s)}
              disabled={activeStatus === s && isListing}
            >
              {s}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {isSuperAdmin ? (
            <Select
              value={templateOrgScope}
              onValueChange={(value) => dispatch(setPropertyTemplatesOrgScope(value as TemplateOrgScope))}
            >
              <SelectTrigger className="h-9 w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="active-org" disabled={!activeOrganizationId}>
                  Active organization
                </SelectItem>
                <SelectItem value="all-orgs">All organizations</SelectItem>
              </SelectContent>
            </Select>
          ) : null}

          <Select value={activeScope} onValueChange={(value) => onChangeActiveScope(value as TemplateScope | 'all')}>
            <SelectTrigger className="h-9 w-[180px]" aria-label="Template scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="organization">Organization</SelectItem>
            </SelectContent>
          </Select>

          {isSystemUser ? (
            <Select value={activeAudience} onValueChange={(v) => onChangeActiveAudience(v as typeof activeAudience)}>
              <SelectTrigger className="h-9 w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="business">Business only</SelectItem>
                <SelectItem value="consumer">Mobile app only</SelectItem>
                <SelectItem value="both">Business + Mobile</SelectItem>
                <SelectItem value="all">All audiences</SelectItem>
              </SelectContent>
            </Select>
          ) : null}

          {canCreateTemplate ? (
            <Button onClick={() => navigate('/templates/add')}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">
                <button
                  type="button"
                  className="hover:text-foreground inline-flex items-center"
                  onClick={() => onSort('title')}
                >
                  TEMPLATE NAME {renderSortArrow('title')}
                </button>
              </TableHead>

              {showOrganizationColumn ? <TableHead className="font-semibold">ORGANIZATION</TableHead> : null}

              {showScopeColumn ? <TableHead className="font-semibold">SCOPE</TableHead> : null}

              <TableHead className="font-semibold">
                <button
                  type="button"
                  className="hover:text-foreground inline-flex items-center"
                  onClick={() => onSort('status')}
                >
                  STATUS {renderSortArrow('status')}
                </button>
              </TableHead>

              <TableHead className="font-semibold">
                <button
                  type="button"
                  className="hover:text-foreground inline-flex items-center"
                  onClick={() => onSort('updatedAt')}
                >
                  LAST MODIFIED DATE {renderSortArrow('updatedAt')}
                </button>
              </TableHead>

              <TableHead className="font-semibold">
                <button
                  type="button"
                  className="hover:text-foreground inline-flex items-center"
                  onClick={() => onSort('audience')}
                >
                  AUDIENCE {renderSortArrow('audience')}
                </button>
              </TableHead>

              <TableHead className="text-right font-semibold">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isWaitingForOrganizationContext && !hasFetched ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} className="text-muted-foreground h-24 text-center">
                  Loading organization context…
                </TableCell>
              </TableRow>
            ) : isListing && !hasFetched ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} className="text-muted-foreground h-24 text-center">
                  Loading templates…
                </TableCell>
              </TableRow>
            ) : error && templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} className="text-muted-foreground h-24 text-center">
                  Unable to load templates.
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} className="text-muted-foreground h-24 text-center">
                  No templates found.
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.title || '(Untitled template)'}</TableCell>
                  {showOrganizationColumn ? (
                    <TableCell className="text-muted-foreground">{template.orgID || '-'}</TableCell>
                  ) : null}
                  {showScopeColumn ? (
                    <TableCell className="text-muted-foreground">
                      {template.scope === 'system' ? 'System' : 'Organization'}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-muted-foreground">{template.status}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatUnixDateTimeCompact(template.updatedAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatAudienceLabel(template.audience)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-4">
                      <Button variant="link" className="h-auto p-0" onClick={() => handleEdit(template.id)}>
                        {canUpdateTemplate && template.status === 'draft' ? 'Edit' : 'View'}
                      </Button>

                      {template.status === 'draft' && canUpdateTemplate ? (
                        <Button
                          variant="link"
                          className="h-auto p-0"
                          disabled={isPublishing}
                          onClick={() => handlePublish(template.id, template.status)}
                        >
                          Publish
                        </Button>
                      ) : null}

                      {(template.status === 'draft' || template.status === 'published') && canCreateTemplate ? (
                        <Button
                          variant="link"
                          className="h-auto p-0"
                          disabled={isCloning}
                          onClick={() => handleClone(template.id, template.status)}
                        >
                          Clone
                        </Button>
                      ) : null}

                      {template.status === 'published' && canUpdateTemplate ? (
                        <>
                          <Button
                            variant="link"
                            className="h-auto p-0"
                            disabled={isDeprecating}
                            onClick={() => handleDeprecate(template.id, template.status)}
                          >
                            Deprecate
                          </Button>
                        </>
                      ) : null}

                      {template.status === 'draft' && canDeleteTemplate ? (
                        <Button
                          variant="link"
                          className="text-destructive hover:text-destructive h-auto p-0"
                          disabled={isDeleting}
                          onClick={() => handleDelete(template.id, template.status)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TemplatesPage;
